import React from 'react';
import { motion } from 'framer-motion';
import { Compass, Search, Globe, Heart } from 'lucide-react';

const ExplorePage: React.FC = () => {
  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="w-20 h-20 bg-accent-wood/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Compass className="w-10 h-10 text-accent-wood" />
          </div>
          <h1 className="text-4xl md:text-6xl font-display font-bold text-white mb-6">
            Explore <span className="text-accent-wood">Legacies</span>
          </h1>
          <p className="text-xl text-primary-300 max-w-3xl mx-auto">
            Discover and explore legacy items from around the world. Find inspiration and connect with other families preserving their heritage.
          </p>
        </motion.div>

        {/* Search Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-primary-800 rounded-2xl shadow-lg border border-primary-700 p-8 mb-12"
        >
          <div className="flex items-center gap-4">
            <Search className="w-6 h-6 text-primary-400" />
            <input
              type="text"
              placeholder="Search for legacy items, families, or categories..."
              className="flex-1 text-lg text-white placeholder-primary-400 focus:outline-none bg-transparent"
            />
            <button className="bg-accent-wood hover:bg-accent-wood-light text-white px-6 py-3 rounded-xl font-semibold transition-colors">
              Search
            </button>
          </div>
        </motion.div>

        {/* Featured Categories */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {[
            { name: 'Family Heirlooms', icon: Heart, count: '1,234' },
            { name: 'Wedding Memories', icon: Globe, count: '567' },
            { name: 'Antique Collections', icon: Compass, count: '890' },
            { name: 'Cultural Heritage', icon: Search, count: '432' },
          ].map((category, index) => (
            <motion.div
              key={category.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-gradient-to-br from-primary-800 to-primary-700 rounded-xl border border-primary-600 p-6 hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="w-12 h-12 bg-accent-wood/20 rounded-lg flex items-center justify-center mb-4">
                <category.icon className="w-6 h-6 text-accent-wood" />
              </div>
              <h3 className="font-semibold text-white mb-2">{category.name}</h3>
              <p className="text-primary-300 text-sm">{category.count} items</p>
            </motion.div>
          ))}
        </div>

        {/* Content Sections */}
        <div className="space-y-12">
          {/* Section 1 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-primary-800 rounded-2xl shadow-lg border border-primary-700 p-8"
          >
            <h2 className="text-3xl font-display font-bold text-white mb-4">
              Discover <span className="text-accent-wood">Collections</span>
            </h2>
            <p className="text-primary-300 text-lg">
              Content coming soon... This section will showcase featured collections and legacy items from around the world.
            </p>
          </motion.div>

          {/* Section 2 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-gradient-to-br from-accent-wood/10 to-accent-wood/20 rounded-2xl border border-accent-wood/30 p-8"
          >
            <h2 className="text-3xl font-display font-bold text-white mb-4">
              Connect with <span className="text-accent-wood">Families</span>
            </h2>
            <p className="text-primary-300 text-lg">
              Content coming soon... This section will help users connect with other families and share their legacy stories.
            </p>
          </motion.div>

          {/* Section 3 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-2xl shadow-lg border border-primary-200 p-8"
          >
            <h2 className="text-3xl font-display font-bold text-primary-900 mb-4">
              Find <span className="text-accent-wood">Inspiration</span>
            </h2>
            <p className="text-primary-600 text-lg">
              Content coming soon... This section will provide inspiration and ideas for preserving your own family legacy.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ExplorePage; 